import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  useCharacterStore,
  useMetaStore,
  useNarrativeStore,
  useWorldStore,
} from '@/engine/state';
import { borrarTodo } from '@/engine/persistence';

/**
 * Se muestra cuando la vida del personaje llega a 0 fuera de combate.
 *
 * Dos caminos:
 *  - "Volver a respirar": revive con 25% de vida y te manda al inicio del
 *    capítulo actual. Permite seguir jugando sin perder progreso general.
 *  - "Empezar de nuevo": wipe completo, nueva partida.
 */
export function GameOverMuerte() {
  const navigate = useNavigate();
  const limpiar = useMetaStore((s) => s.limpiarGameOverMuerte);

  const revivir = () => {
    const store = useCharacterStore.getState();
    const vidaMax = store.personaje.stats.vidaMax;
    const vidaAprox = Math.max(1, Math.floor(vidaMax * 0.25));
    useCharacterStore.setState((s) => ({
      personaje: {
        ...s.personaje,
        stats: { ...s.personaje.stats, vida: vidaAprox },
      },
    }));
    store.persistir();

    // Volver al inicio del capítulo actual para no quedar atascado.
    const capitulo = useNarrativeStore.getState().capituloActual;
    const escenaInicio = `cap${capitulo}_intro`;
    useNarrativeStore.setState({
      sceneIdActual: escenaInicio,
      historialEscenas: [...useNarrativeStore.getState().historialEscenas, escenaInicio],
    });
    useNarrativeStore.getState().persistir();

    limpiar();
    navigate('/game/story');
  };

  const nuevaPartida = () => {
    borrarTodo();
    useCharacterStore.getState().reiniciar();
    useNarrativeStore.getState().reiniciar();
    useWorldStore.getState().reiniciar();
    limpiar();
    navigate('/menu');
  };

  return (
    <div className="relative h-full w-full flex items-center justify-center overflow-hidden bg-ash-950">
      <div className="absolute inset-0 bg-vignette" aria-hidden />
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            'radial-gradient(ellipse at 50% 60%, rgba(237,95,30,0.18), transparent 55%)',
            'radial-gradient(ellipse at 50% 60%, rgba(217,69,21,0.12), transparent 55%)',
            'radial-gradient(ellipse at 50% 60%, rgba(237,95,30,0.18), transparent 55%)',
          ],
        }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        aria-hidden
      />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
        className="relative z-10 max-w-lg px-6 text-center space-y-5"
      >
        <motion.h1
          className="text-display fluid-3xl text-ember-300 tracking-[0.2em]"
          animate={{ opacity: [0.85, 1, 0.85] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        >
          EL CUERPO CEDE
        </motion.h1>

        <div className="text-narrative text-ash-200 fluid-base italic space-y-3">
          <p>El aliento se fue.</p>
          <p>
            El bosque no se detiene por eso. Pipo aúlla un nombre que ya no escuchás.
            Alguien te va a encontrar, o no. Todavía hay tiempo de volver, si elegís
            volver.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={revivir}
            type="button"
            className="px-5 py-2.5 border border-moss-600 text-moss-200 font-display tracking-widest uppercase fluid-xs hover:bg-moss-900/40 transition-colors"
          >
            Volver a respirar
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={nuevaPartida}
            type="button"
            className="px-5 py-2.5 border border-ember-600 text-ember-200 font-display tracking-widest uppercase fluid-xs hover:bg-ember-900/40 transition-colors"
          >
            Empezar de nuevo
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
}
